<template>
  <div class="dashboard-page">
    <v-progress-linear
      v-if="store.loading"
      indeterminate
      color="primary"
      class="mb-3"
    />

    <GameStats
      :currentDay="store.currentDay"
      :totalDays="store.totalDays"
      :points="currentPlayerPoints"
      :budget="store.budget"
      :reservedBudget="store.reservedBudget"
      :creditLimit="store.creditLimit"
      :spendingCapacity="store.spendingCapacity"
      :inDebt="store.inDebt"
      :rank="store.currentPlayerRank"
      :totalPlayers="store.totalPlayers"
      :league-status="currentArea?.status || ''"
      :league-end-at="currentArea?.endAt || null"
    />

    <v-alert v-if="store.error" type="error" class="mt-3">
      {{ store.error }}
    </v-alert>

    <div class="dashboard-nav-wrapper mt-4">
      <v-bottom-navigation
        v-model="store.activeTab"
        grow
        class="dashboard-nav"
      >
        <v-btn value="areas" class="dashboard-nav-btn">
          <span class="material-symbols-outlined dashboard-nav-icon">
            public
          </span>
          <span class="dashboard-nav-label">Ligas</span>
        </v-btn>

        <v-btn
          value="mapa"
          class="dashboard-nav-btn"
          :disabled="!leagueHasStarted"
        >
          <span class="material-symbols-outlined dashboard-nav-icon">
            dashboard
          </span>
          <span class="dashboard-nav-label">Panel de control</span>
        </v-btn>

        <v-btn
          value="rankings"
          class="dashboard-nav-btn"
          :disabled="!leagueHasStarted"
        >
          <span class="material-symbols-outlined dashboard-nav-icon">
            leaderboard
          </span>
          <span class="dashboard-nav-label">Clasificación</span>
        </v-btn>

        <v-btn
          value="company"
          class="dashboard-nav-btn"
          :disabled="!leagueHasStarted"
        >
          <span class="material-symbols-outlined dashboard-nav-icon">
            grid_on
          </span>
          <span class="dashboard-nav-label">Plantilla</span>
        </v-btn>

        <v-btn
          value="market"
          class="dashboard-nav-btn"
          :disabled="!leagueHasStarted"
        >
          <span class="material-symbols-outlined dashboard-nav-icon">
            local_mall
          </span>
          <span class="dashboard-nav-label">Mercado</span>
        </v-btn>

        <v-btn
          value="activity"
          class="dashboard-nav-btn"
          :disabled="!leagueHasStarted"
        >
          <span class="material-symbols-outlined dashboard-nav-icon">
            history
          </span>
          <span class="dashboard-nav-label">Actividad</span>
        </v-btn>

        <v-btn
          value="minijuegos"
          class="dashboard-nav-btn"
          :disabled="!leagueHasStarted"
        >
          <span class="material-symbols-outlined dashboard-nav-icon">
            joystick
          </span>
          <span class="dashboard-nav-label">Minijuegos</span>
        </v-btn>

        <v-btn
          value="about"
          class="dashboard-nav-btn"
        >
          <span class="material-symbols-outlined dashboard-nav-icon">
            info
          </span>
          <span class="dashboard-nav-label">Sobre Simulart</span>
        </v-btn>
      </v-bottom-navigation>
    </div>

    <div class="dashboard-content mt-4">
      <MyAreas
        v-if="store.activeTab === 'areas'"
        :areas="store.areas"
        :current-area-id="store.currentAreaId"
        @enter-area="handleEnterArea"
        @leave-area="handleLeaveArea"
        @delete-area="handleDeleteArea"
        @create-area="handleCreateArea"
        @join-area-code="handleJoinAreaByCode"
        @start-area="handleStartArea"
      />
      <Rankings
        v-if="leagueHasStarted && store.activeTab === 'rankings'"
        :players="store.players"
        :currentPlayerId="store.currentPlayerId"
      />

      <PanelControl
        v-if="leagueHasStarted && store.activeTab === 'mapa'"
      />

      <Company
        v-if="leagueHasStarted && store.activeTab === 'company'"
        :generators="store.ownedGenerators"
        :totalPoints="store.totalPoints"
        :budget="store.budget"
        @refresh="store.refreshCompany"
        @sell-market="store.createSaleFromCompany"
        @sell-instant="store.sellOwnedGenerator"
      />

      <Market
        v-if="leagueHasStarted && store.activeTab === 'market'"
        :generators="store.generators"
        :budget="store.spendingCapacity"
        @bid-generator="store.bidGenerator"
        @cancel-bid-generator="store.cancelBidGenerator"
        @create-sale="store.createSale"
        @refresh-budget="store.refreshStats"
      />

      <MenuMiniGames
        v-if="leagueHasStarted && store.activeTab === 'minijuegos'"
      />

      <Activity
        v-if="leagueHasStarted && store.activeTab === 'activity'"
        :events="store.events"
        :dailyPoints="store.dailyPoints"
        :weekly-prizes="store.weeklyPrizes"
      />

      <AboutUs v-if="store.activeTab === 'about'" />
    </div>
  </div>
</template>

<script>
import { useGameStore } from "@/stores/game";
import Swal from "sweetalert2";

import GameStats from "@/components/GameStats.vue";
import MyAreas from "@/components/MyAreas.vue";
import Rankings from "@/components/Rankings.vue";
import Company from "@/components/Company.vue";
import Market from "@/components/Market.vue";
import Activity from "@/components/Activity.vue";
import AboutUs from "@/components/AboutUs.vue";
import PanelControl from "@/components/PanelControl.vue";
import MenuMiniGames from "@/components/minigames/MenuMiniGames.vue";

export default {
  name: "DashboardView",

  components: {
    GameStats,
    MyAreas,
    Rankings,
    Company,
    Market,
    Activity,
    AboutUs,
    PanelControl,
    MenuMiniGames,
  },

  data() {
    return {
      store: useGameStore(),
    };
  },

  async mounted() {
    await this.store.loadAll();
  },

  computed: {
    hasActiveLeague() {
  return this.store.hasActiveLeague;
},
    currentArea() {
  return this.store.areas.find(
    (area) =>
      String(area.id) ===
      String(this.store.currentAreaId)
  ) || null;
},
leagueHasStarted() {
  if (!this.hasActiveLeague || !this.currentArea) {
    return false;
  }

  if (
    this.currentArea.status !== "active" ||
    this.currentArea.hasEnded
  ) {
    return false;
  }

  if (
    this.currentArea.endAt &&
    Date.now() >= new Date(this.currentArea.endAt).getTime()
  ) {
    return false;
  }

  return true;
},
    currentPlayerPoints() {
      const me = this.store.players.find(
        (p) => String(p.id) === String(this.store.currentPlayerId)
      );

      return me?.points ?? this.store.totalPoints ?? 0;
    },
  },
 watch: {
  "store.activeTab": {
    async handler(newTab, oldTab) {
      const tabsConLiga = [
        "mapa",
        "rankings",
        "company",
        "market",
        "activity",
        "minijuegos",
      ];

      if (
        tabsConLiga.includes(newTab) &&
        !this.leagueHasStarted
      ) {
        this.store.activeTab = "areas";
        return;
      }

      if (
        newTab !== "company" ||
        newTab === oldTab
      ) {
        return;
      }

      const ligaId = this.store.currentAreaId;

      if (!ligaId) {
        this.store.activeTab = "areas";
        return;
      }

      try {
        await Promise.all([
          this.store.refreshCompany(ligaId),
          this.store.refreshGenerators(ligaId),
          this.store.refreshStats(ligaId),
        ]);
      } catch (error) {
        console.error(
          "No se pudo actualizar la plantilla:",
          error
        );
      }
    },
  },

  leagueHasStarted(active) {
    if (!active) {
      const tabsConLiga = [
        "mapa",
        "rankings",
        "company",
        "market",
        "activity",
        "minijuegos",
      ];

      if (tabsConLiga.includes(this.store.activeTab)) {
        this.store.activeTab = "areas";
      }
    }
  },
},

  methods: {
   async handleStartArea({
  areaId,
  durationDays,
  done,
  fail,
}) {
  try {
    await this.store.startArea(
      areaId,
      durationDays
    );

    if (typeof done === "function") {
      done();
    }
  } catch (error) {
    if (typeof fail === "function") {
      fail(
        error?.response?.data?.message ||
        error?.response?.data?.data?.message ||
        error?.message ||
        "No se pudo iniciar la liga."
      );
    }
  }
},
    async handleEnterArea(payload) {
    const areaId = payload?.areaId ?? payload;
    const done =
      typeof payload?.done === "function"
        ? payload.done
        : () => {};

    const fail =
      typeof payload?.fail === "function"
        ? payload.fail
        : () => {};

    try {
      const selectedArea = this.store.areas.find(
      area => String(area.id) === String(areaId)
    );

    await this.store.enterArea(
      areaId,
      selectedArea?.status === "active" && !selectedArea?.hasEnded
        ? "rankings"
        : "areas"
    );

      done();
    } catch (error) {
      fail(
        error?.response?.data?.message ||
        error?.response?.data?.data?.message ||
        error?.message ||
        "No se pudo entrar en la liga."
      );

      await Swal.fire({
        icon: "error",
        title: "No se pudo entrar",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "No se pudo entrar en la liga.",
        confirmButtonText: "Entendido",
        customClass: {
          confirmButton: "clausulazo-swal-confirm",
        },
      });
    }
  },

  async handleCreateArea({
    name,
    durationDays,
    done,
    fail,
  }) {
    try {
      await this.store.createArea({
        name,
        durationDays,
      });

      if (typeof done === "function") {
        done();
      }
    } catch (error) {
      if (typeof fail === "function") {
        fail(
          error?.response?.data?.message ||
          error?.response?.data?.data?.message ||
          error?.message ||
          "No se pudo crear la liga."
        );
      }
    }
  },

  async handleJoinAreaByCode({
    code,
    done,
    fail,
  }) {
    try {
      await this.store.joinAreaByCode(code);

      if (typeof done === "function") {
        done();
      }
    } catch (error) {
      if (typeof fail === "function") {
        fail(
          error?.response?.data?.message ||
          error?.response?.data?.data?.message ||
          error?.message ||
          "No se pudo unir a la liga."
        );
      }
    }
  },

  async handleLeaveArea({
    areaId,
    done,
    fail,
  }) {
    try {
      await this.store.leaveArea(areaId);

      if (typeof done === "function") {
        done();
      }
    } catch (error) {
      if (typeof fail === "function") {
        fail(
          error?.response?.data?.message ||
          error?.response?.data?.data?.message ||
          error?.message ||
          "No se pudo salir de la liga."
        );
      }
    }
  },
  async handleDeleteArea({
  areaId,
  done,
  fail,
}) {
  try {
    await this.store.deleteArea(areaId);

    if (typeof done === "function") {
      done();
    }
  } catch (error) {
    if (typeof fail === "function") {
      fail(
        error?.response?.data?.message ||
        error?.response?.data?.data?.message ||
        error?.message ||
        "No se pudo eliminar la liga."
      );
    }
  }
},
    
    

    
  },
};
</script>

<style scoped>
.dashboard-page {
  width: 100%;
}

.dashboard-nav-wrapper {
  width: 100%;
}

.dashboard-nav {
  border-radius: 22px !important;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.72) !important;
  border: 1px solid rgba(49, 99, 95, 0.12);
  box-shadow: 0 12px 35px rgba(49, 99, 95, 0.11);
  backdrop-filter: blur(14px);
}

::v-deep .v-bottom-navigation .v-btn.v-btn--active {
  background: linear-gradient(135deg, #bfe7d2, #d7f4e4) !important;
  border-radius: 18px !important;
}

::v-deep .v-bottom-navigation .v-btn.v-btn--active span {
  color: #2e7d32 !important;
  font-weight: 600;
}

::v-deep .v-bottom-navigation .v-btn:not(.v-btn--active) span {
  color: #666;
}

@media (max-width: 960px) {
  .dashboard-nav-wrapper {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    border-radius: 0;
  }

  .dashboard-nav-wrapper::-webkit-scrollbar {
    height: 4px;
  }

  .dashboard-nav {
    min-width: 760px;
    height: 48px !important;
  }

  ::v-deep .dashboard-nav .v-btn {
    min-width: 95px !important;
    height: 48px !important;
    padding: 0 6px !important;
  }

  .dashboard-nav-icon {
    font-size: 18px !important;
    line-height: 1;
  }

  .dashboard-nav-label {
    font-size: 10px !important;
    line-height: 1.1;
    white-space: nowrap;
  }

  .dashboard-content {
    margin-top: 12px !important;
  }
}

@media (max-width: 600px) {
  .dashboard-nav-wrapper {
    overflow-x: hidden;
  }

  .dashboard-nav {
    min-width: 0 !important;
    width: 100% !important;
    height: auto !important;
    display: grid !important;
    grid-template-columns: repeat(4, 1fr);
    gap: 4px;
    padding: 4px;
    border-radius: 20px !important;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.72) !important;
    border: 1px solid rgba(49, 99, 95, 0.12);
    box-shadow: 0 12px 35px rgba(49, 99, 95, 0.11);
  }

  ::v-deep .dashboard-nav .v-btn {
    min-width: 0 !important;
    width: 100% !important;
    height: 52px !important;
    padding: 0 2px !important;
  }

  .dashboard-nav-icon {
    font-size: 18px !important;
    line-height: 1;
  }

  .dashboard-nav-label {
    font-size: 9px !important;
    line-height: 1.1;
    white-space: normal;
    text-align: center;
  }
}

@media (max-width: 380px) {
  .dashboard-nav {
    grid-template-columns: repeat(4, 1fr);
  }

  ::v-deep .dashboard-nav .v-btn {
    height: 50px !important;
  }

  .dashboard-nav-icon {
    font-size: 16px !important;
  }

  .dashboard-nav-label {
    font-size: 8px !important;
  }
}
</style>
